import { redirect } from 'react-router';

/**
 * Browser → crm-api, cross-origin with credentials.
 *
 *  - auth_token / refresh_token are httpOnly cookies the API sets; JS never sees them.
 *  - The CSRF token comes back in JSON (login / refresh / session) and is kept in memory
 *    here; it is sent as X-CSRF-Token on every unsafe request.
 *  - A 401 on a normal call triggers ONE refresh (deduplicated across concurrent callers)
 *    and a retry; if that fails the caller is bounced to /login?next=…
 */
export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8100').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export interface SessionUser {
  email: string;
  name?: string;
  role: 'admin' | 'editor';
}

export interface SessionResponse {
  user: SessionUser;
  csrfToken: string;
}

let csrfToken: string | null = null;
let currentUser: SessionUser | null = null;
let refreshing: Promise<boolean> | null = null;

export function setSession(session: SessionResponse | null): void {
  csrfToken = session?.csrfToken ?? null;
  currentUser = session?.user ?? null;
}

export function getSessionUser(): SessionUser | null {
  return currentUser;
}

export function loginRedirect(): Response {
  const here = `${window.location.pathname}${window.location.search}`;
  const next = here && here !== '/' ? `?next=${encodeURIComponent(here)}` : '';
  return redirect(`/login${next}`);
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

type ApiInit = Omit<RequestInit, 'body'> & {
  /** JSON body (sets content-type). */
  json?: unknown;
  body?: BodyInit | null;
  /** false for the auth endpoints themselves: a 401 there is the answer, not a trigger. */
  auth?: boolean;
};

async function rawFetch(path: string, init: ApiInit): Promise<Response> {
  const { json, auth: _auth, headers: extraHeaders, ...rest } = init;
  const method = (rest.method ?? 'GET').toUpperCase();
  const headers = new Headers(extraHeaders ?? {});
  headers.set('accept', 'application/json');
  if (json !== undefined) headers.set('content-type', 'application/json');
  if (!SAFE_METHODS.has(method) && csrfToken) headers.set('x-csrf-token', csrfToken);
  return fetch(`${API_URL}${path}`, {
    ...rest,
    method,
    credentials: 'include',
    headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
}

function parseBody(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Single in-flight refresh shared by every caller that hits a 401 at the same time. */
export function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await rawFetch('/auth/refresh', { method: 'POST', auth: false });
        if (!res.ok) return false;
        setSession((await res.json()) as SessionResponse);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export async function api<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
  let response = await rawFetch(path, init);

  if (response.status === 401 && init.auth !== false) {
    const refreshed = await refreshOnce();
    if (refreshed) response = await rawFetch(path, init);
    if (response.status === 401) {
      setSession(null);
      if (!window.location.pathname.startsWith('/login')) throw loginRedirect();
    }
  }

  const data = parseBody(await response.text());
  if (!response.ok) {
    const body = data && typeof data === 'object' ? (data as { message?: unknown; code?: unknown }) : {};
    const message = typeof body.message === 'string' ? body.message : `Request failed (${response.status})`;
    throw new ApiError(response.status, message, typeof body.code === 'string' ? body.code : undefined);
  }
  return data as T;
}

/** `?a=1&b=2` from an object, skipping empty values. */
export function toQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/**
 * Error → user-facing text. A redirect Response thrown by `api()` inside an event handler
 * (outside a loader) is followed here so the user still lands on /login.
 */
export function errMsg(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof Response) {
    const location = err.headers.get('Location');
    if (location) window.location.assign(location);
    return 'Session expired — redirecting to login…';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
